//
// @mve/protocol — shared types for the cross-window postMessage
// channel between the embed (in the customer's page) and the
// receiver app (loaded from our origin).
//
// This package has no runtime beyond a tiny type guard — it's
// basically a contract definition that both sides import so they
// can't drift apart.
//

//
// Where the receiver app lives in development. Production builds
// substitute this via Vite's define config.
//
export const MVE_APP_ORIGIN_DEV = 'http://localhost:5273';

//
// The route on the app origin that listens for embed messages and
// displays the final video.
//
export const MVE_RESULT_PATH = '/result';

//
// License flags served by /api/embed/license. Propagated from the
// embed to the receiver so the receiver's resolution picker can
// hide or disable presets above the tier's maxWidth. The values
// themselves are advisory — real enforcement happens server-side
// at credit-consume time (step 5) — but they keep the UI honest.
//
export interface LicenseInfo {
  tier: 'free' | 'paid';
  allowHires: boolean;
  maxWidth: number;
  watermark: boolean;
  creditsRemaining: number;
}

//
// Metadata the embed sends at the *start* of a render, before any
// frames are produced. Lets the receiver display what's coming
// (dimensions, frame count) while the progress bar fills.
//
// `license` is attached so the receiver's resolution picker knows
// which presets to offer. Optional for back-compat with any older
// embed builds that haven't been updated.
//
export interface RenderStartMeta {
  durationSeconds: number;
  width: number;
  height: number;
  fps: number;
  totalFrames: number;
  license?: LicenseInfo;
}

//
// Metadata the embed sends alongside the final MP4 blob. Similar to
// RenderStartMeta but with a MIME type for the blob; totalFrames is
// dropped since the blob itself speaks for what was produced.
//
export interface RenderMeta {
  durationSeconds: number;
  width: number;
  height: number;
  fps: number;
  mime: string;
}

//
// Messages the embed sends to the receiver over the lifetime of a
// render:
//
//   render-start     → the render has begun; here's what to expect.
//   render-progress  → we're N% of the way through.
//   render-result    → finished; here's the MP4 blob and its metadata.
//   render-error     → render failed; here's a human-readable message.
//
// Exactly one of (render-result | render-error) fires per render.
//
export type EmbedToApp =
  | { kind: 'mve/render-start'; meta: RenderStartMeta }
  | { kind: 'mve/render-progress'; fraction: number }
  | { kind: 'mve/render-result'; blob: Blob; meta: RenderMeta }
  | { kind: 'mve/render-error'; message: string };

//
// Messages the receiver sends back to the embed.
//
//   receiver-ready   handshake: React mounted, it's safe to send.
//                    The embed buffers everything until this arrives
//                    so we don't drop early messages while the
//                    receiver's app is still bootstrapping.
//
//   request-render   the user picked a resolution in the receiver
//                    and wants a fresh render at that width. Height
//                    is derived from the map's current aspect ratio
//                    on the embed side.
//
export type AppToEmbed =
  | { kind: 'mve/receiver-ready' }
  | { kind: 'mve/request-render'; width: number };

//
// Union of all messages in either direction. Primarily used by
// isMveMessage for narrowing incoming MessageEvent data.
//
export type MveMessage = EmbedToApp | AppToEmbed;

//
// Guard for narrowing untyped MessageEvent.data. Cross-origin
// windows can postMessage anything at us, so we reject anything
// that doesn't match our protocol envelope (an object whose `kind`
// starts with `mve/`) before interpreting the payload.
//
export function isMveMessage(value: unknown): value is MveMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in value &&
    typeof (value as { kind: unknown }).kind === 'string' &&
    (value as { kind: string }).kind.startsWith('mve/')
  );
}
